import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Search, CheckCircle2, Car, User, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { NumberPlate } from "@/components/NumberPlate";
import { CustomerPicker } from "@/components/CustomerPicker";
import { toast } from "sonner";

const norm = (v) => (v || "").toUpperCase().replace(/\s+/g, "");

export default function JobNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [categories, setCategories] = useState([]);

  const [vrm, setVrm] = useState("");
  const [looking, setLooking] = useState(false);
  const [dvlaData, setDvlaData] = useState(null); // details for a NEW vehicle
  const [vehicle, setVehicle] = useState(null);   // selected/created vehicle (DB doc)
  const [customer, setCustomer] = useState(null);
  const [creatingVeh, setCreatingVeh] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [complaint, setComplaint] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/jobs/categories").then((r) => {
      const flat = [];
      (r.groups || []).forEach((g) => (g.categories || []).forEach((c) => flat.push({ id: c.id, label: `${g.label} · ${c.label}` })));
      setCategories(flat);
    }).catch(() => {});
  }, []);

  // If arriving with ?vehicle_id=, preload that vehicle (from Vehicle detail "Create Job")
  useEffect(() => {
    const vid = params.get("vehicle_id");
    if (vid) selectVehicle(vid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectVehicle = async (vid) => {
    try {
      const v = await api(`/vehicles/${vid}`);
      setVehicle(v); setDvlaData(null); setVrm(v.vrm);
      if (v.customer_id) { const c = await api(`/customers/${v.customer_id}`); setCustomer(c); }
    } catch { /* ignore */ }
  };

  const lookup = async () => {
    if (!vrm.trim()) return;
    setLooking(true); setDvlaData(null); setVehicle(null); setCustomer(null);
    try {
      const existing = await api(`/vehicles?q=${encodeURIComponent(vrm.trim())}`);
      const match = (existing || []).find((v) => norm(v.vrm) === norm(vrm));
      if (match) {
        setVehicle(match);
        if (match.customer_id) { const c = await api(`/customers/${match.customer_id}`); setCustomer(c); }
        toast.success("Vehicle found in your records");
        return;
      }
      const d = await api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`);
      setDvlaData(d);
      toast.success("Found on DVLA — add it to create the job");
    } catch (e) {
      toast.error(e.message || "Vehicle not found");
    } finally { setLooking(false); }
  };

  const createVehicle = async () => {
    setCreatingVeh(true);
    try {
      const v = await api("/vehicles", { method: "POST", body: JSON.stringify({
        vrm: vrm.trim(), make: dvlaData.make || "", model: dvlaData.model || "",
        year: dvlaData.year || null, colour: dvlaData.colour || null, fuel_type: dvlaData.fuel_type || null,
        engine_capacity: dvlaData.engine_capacity || null, mot_expiry: dvlaData.mot_expiry || null, tax_expiry: dvlaData.tax_expiry || null,
        customer_id: customer?.id || null,
      }) });
      setVehicle(v); setDvlaData(null);
      toast.success("Vehicle added");
    } catch (e) { toast.error(e.message); } finally { setCreatingVeh(false); }
  };

  // Link chosen customer to the selected vehicle (persists the relationship)
  const linkCustomer = async (c) => {
    setCustomer(c);
    if (vehicle && c && vehicle.customer_id !== c.id) {
      try { await api(`/vehicles/${vehicle.id}`, { method: "PATCH", body: JSON.stringify({ customer_id: c.id }) }); setVehicle({ ...vehicle, customer_id: c.id }); }
      catch { /* non-fatal */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicle) { toast.error("Select a vehicle first"); return; }
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const j = await api("/jobs", { method: "POST", body: JSON.stringify({
        vehicle_id: vehicle.id, customer_id: customer?.id || vehicle.customer_id || null,
        title: title.trim(), category: category || null, complaint: complaint || "", status: "open",
      }) });
      toast.success("Job created");
      navigate(`/jobs/${j.id}`);
    } catch (e2) {
      if (e2.status === 402 && e2.body?.detail?.error === "plan_limit_reached") {
        toast.error(e2.body.detail.message || "Monthly job limit reached — upgrade to Pro.");
        navigate("/billing");
      } else { toast.error(e2.message || "Could not create job"); }
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/jobs" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Jobs</Link>
      <PageHeader title="New Job" subtitle="Start with the vehicle registration" />

      {/* Step 1 — VRM */}
      <div className="mb-4 rounded-xl border border-border bg-card p-6">
        <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">STEP 1 · VEHICLE REGISTRATION</span>
        <div className="flex gap-2">
          <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), lookup())} placeholder="AB12 XYZ" data-testid="job-vrm-input"
            className="flex-1 rounded-lg border border-input bg-plate px-3.5 py-3 text-center font-mono text-xl font-bold uppercase tracking-wider text-black outline-none focus:ring-2 focus:ring-brand/40" />
          <button type="button" onClick={lookup} disabled={looking} data-testid="job-vrm-lookup-btn" className="inline-flex items-center gap-2 rounded-lg bg-midnight px-4 py-3 text-sm font-bold text-white disabled:opacity-60">
            {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Find
          </button>
        </div>

        {/* New vehicle from DVLA */}
        {dvlaData && !vehicle && (
          <div className="mt-4 rounded-lg border border-border bg-secondary p-4" data-testid="job-dvla-result">
            <div className="flex items-center justify-between">
              <NumberPlate vrm={vrm} size="sm" />
              <p className="font-bold text-midnight">{dvlaData.make} {dvlaData.model}</p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{[dvlaData.year, dvlaData.colour, dvlaData.fuel_type].filter(Boolean).join(" · ")}</p>
            <button type="button" onClick={createVehicle} disabled={creatingVeh} data-testid="job-add-vehicle-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {creatingVeh ? <Loader2 className="h-4 w-4 animate-spin" /> : <Car className="h-4 w-4" />}Add vehicle & continue
            </button>
          </div>
        )}
      </div>

      {/* Step 2 — vehicle + customer */}
      {vehicle && (
        <>
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-success/40 bg-success/5 p-4" data-testid="job-selected-vehicle">
            <CheckCircle2 className="h-5 w-5 text-success" />
            <NumberPlate vrm={vehicle.vrm} size="sm" />
            <div className="min-w-0 flex-1"><p className="truncate font-bold text-midnight">{vehicle.make} {vehicle.model}</p><p className="text-xs text-muted-foreground">{[vehicle.year, vehicle.colour].filter(Boolean).join(" · ")}</p></div>
          </div>

          <div className="mb-4 rounded-xl border border-border bg-card p-5">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-muted-foreground"><User className="h-3.5 w-3.5" />CUSTOMER</span>
            {customer ? (
              <div className="flex items-center justify-between rounded-lg bg-secondary p-3">
                <div className="flex items-center gap-2"><div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-tertiary text-sm font-extrabold text-brand">{customer.name.charAt(0).toUpperCase()}</div><span className="text-sm font-bold text-midnight">{customer.name}</span></div>
                <button type="button" onClick={() => setCustomer(null)} data-testid="job-change-customer" className="text-xs font-semibold text-brand-2">Change</button>
              </div>
            ) : (
              <>
                <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Link2 className="h-3.5 w-3.5" />No customer linked — add or select one:</p>
                <CustomerPicker value={null} onChange={linkCustomer} />
              </>
            )}
          </div>

          {/* Step 3 — job details (unchanged fields) */}
          <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-6">
            <span className="block text-[11px] font-bold tracking-widest text-muted-foreground">STEP 3 · JOB DETAILS</span>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">TITLE *</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="job-title-input" placeholder="Front brake pads & discs" className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CATEGORY</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="job-category-select" className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-sm outline-none focus:border-brand">
                <option value="">Uncategorised</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER COMPLAINT</span>
              <textarea value={complaint} onChange={(e) => setComplaint(e.target.value)} data-testid="job-complaint-input" rows={3} placeholder="What did the customer report?" className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" /></label>
            <button type="submit" disabled={saving} data-testid="job-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "CREATE JOB"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
